import React from 'react';
import './App.css';
import StaffHeader from './StaffHeader';
import { getStaffPortalSession } from './staffPortalSession';
import { useStaffPortalGuard } from './useStaffPortalGuard';
import { supabase } from './supabaseClient';

const STATUS_LABEL = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
};

function initials(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

function displayProperty(row) {
  const rel = row.properties;
  const p = Array.isArray(rel) ? rel[0] : rel;
  if (p?.name) return p.location ? `${p.name} (${p.location})` : p.name;
  return (row.property_name || '').trim() || '—';
}

function statusDashClass(statusKey) {
  if (statusKey === 'in_progress') return 'in-progress';
  return statusKey;
}

function mapRow(row) {
  const media = row.maintenance_request_media;
  const n = Array.isArray(media) ? media.length : 0;
  const statusKey = row.status || 'pending';
  return {
    id: row.id,
    tenant: (row.tenant_name || '').trim() || '—',
    property: `${displayProperty(row)} · Room ${(row.room_label || '').trim() || '—'}`,
    issue: (row.issue_description || '').trim() || '—',
    media: n === 0 ? 'No attachments' : `${n} attachment${n === 1 ? '' : 's'}`,
    statusKey,
    statusLabel: STATUS_LABEL[statusKey] || statusKey,
  };
}

function StaffDashboard() {
  useStaffPortalGuard();
  const session = getStaffPortalSession();
  const staffId = session?.staffId;
  const displayName = session?.fullName?.trim() || 'Maintenance staff';
  const displayRole = session?.role?.trim() || 'On duty';

  const [tasks, setTasks] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [listError, setListError] = React.useState('');
  const [statusBusyId, setStatusBusyId] = React.useState(null);

  const loadTasks = React.useCallback(async () => {
    const sid = getStaffPortalSession()?.staffId;
    if (!supabase || !sid) {
      setTasks([]);
      setListError(supabase ? '' : 'Supabase is not configured.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setListError('');
    const { data, error } = await supabase
      .from('maintenance_requests')
      .select(
        `
        id,
        tenant_name,
        property_name,
        room_label,
        issue_description,
        status,
        properties (
          name,
          location
        ),
        maintenance_request_media (
          id
        )
      `
      )
      .eq('assigned_staff_id', sid)
      .order('created_at', { ascending: false });

    if (error) {
      setListError(
        error.message ||
          'Could not load tasks. If assignments fail, run sql/add_maintenance_assigned_staff_supabase.sql.'
      );
      setTasks([]);
    } else {
      setTasks((data || []).map(mapRow));
    }
    setLoading(false);
  }, []);

  React.useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const updateStatus = async (taskId, nextStatus) => {
    if (!supabase || !staffId) return;
    setStatusBusyId(taskId);
    try {
      const { error } = await supabase
        .from('maintenance_requests')
        .update({ status: nextStatus })
        .eq('id', taskId)
        .eq('assigned_staff_id', staffId);
      if (error) throw error;
      await loadTasks();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.message || 'Could not update status.');
    } finally {
      setStatusBusyId(null);
    }
  };

  const openCount = tasks.filter((t) => t.statusKey !== 'completed').length;
  const completedCount = tasks.filter((t) => t.statusKey === 'completed').length;
  const pendingCount = tasks.filter((t) => t.statusKey === 'pending').length;
  const inProgressCount = tasks.filter((t) => t.statusKey === 'in_progress').length;

  return (
    <div className="staff-dash-root">
      <StaffHeader />

      <header className="staff-dash-topbar">
        <div className="staff-dash-left">
          <div className="staff-dash-logo">ST</div>
          <div className="staff-dash-text">
            <span className="staff-dash-title">Staff workspace</span>
            <span className="staff-dash-subtitle">Daily maintenance tasks</span>
          </div>
        </div>
        <div className="staff-dash-right">
          <div className="staff-dash-profile">
            <div className="staff-dash-avatar">{initials(displayName)}</div>
            <div className="staff-dash-profile-meta">
              <span className="staff-dash-name">{displayName}</span>
              <span className="staff-dash-role">{displayRole}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="staff-dash-layout">
        <section className="staff-dash-main">
          <section className="staff-dash-panel staff-dash-panel--notifications">
            <h2 className="staff-dash-panel-title">Notifications</h2>
            <ul className="staff-dash-notifications">
              <li>
                <span className="staff-dash-notify-title">Assigned work</span>
                <span className="staff-dash-notify-sub">
                  Tasks listed below are the requests an admin has assigned to you
                  (linked staff on Maintenance).
                </span>
              </li>
            </ul>
          </section>

          <section className="staff-dash-panel">
            <div className="staff-dash-panel-header">
              <div>
                <h2 className="staff-dash-panel-title">Assigned tasks</h2>
                <p className="staff-dash-panel-subtitle">
                  Update status as you progress through your work.
                </p>
              </div>
              <span className="staff-dash-tag">{openCount} open</span>
            </div>

            {listError ? (
              <p className="properties-save-error" role="alert" style={{ margin: '8px 0 0' }}>
                {listError}
              </p>
            ) : null}

            {loading ? (
              <p className="properties-list-empty" style={{ marginTop: 12 }}>
                Loading your tasks…
              </p>
            ) : null}

            {!loading && !listError && tasks.length === 0 ? (
              <p className="properties-list-empty" style={{ marginTop: 12 }}>
                No tasks assigned to you yet. When an admin assigns a maintenance request
                to your account, it will show up here.
              </p>
            ) : null}

            {!loading && tasks.length > 0 ? (
              <ul className="staff-dash-task-list">
                {tasks.map((task) => (
                  <li key={task.id} className="staff-dash-task-card">
                    <div className="staff-dash-task-main">
                      <div className="staff-dash-task-heading">
                        <span className="staff-dash-task-tenant">{task.tenant}</span>
                        <span
                          className={`staff-dash-status staff-dash-status--${statusDashClass(
                            task.statusKey
                          )}`}
                        >
                          {task.statusLabel}
                        </span>
                      </div>
                      <p className="staff-dash-task-property">{task.property}</p>
                      <p className="staff-dash-task-issue">{task.issue}</p>
                      <p className="staff-dash-task-media">📎 {task.media}</p>
                    </div>
                    {task.statusKey !== 'completed' ? (
                      <div className="staff-dash-task-actions">
                        <button
                          type="button"
                          className="btn-ghost staff-dash-btn"
                          disabled={
                            statusBusyId === task.id || task.statusKey === 'in_progress'
                          }
                          onClick={() => updateStatus(task.id, 'in_progress')}
                        >
                          {statusBusyId === task.id ? 'Saving…' : 'In progress'}
                        </button>
                        <button
                          type="button"
                          className="btn-primary staff-dash-btn staff-dash-btn--primary"
                          disabled={statusBusyId === task.id}
                          onClick={() => updateStatus(task.id, 'completed')}
                        >
                          {statusBusyId === task.id ? 'Saving…' : 'Mark completed'}
                        </button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        </section>

        <aside className="staff-dash-side">
          <section className="staff-dash-panel">
            <h2 className="staff-dash-panel-title">Profile</h2>
            <p className="staff-dash-panel-subtitle">
              Quick overview of your shift and workload.
            </p>
            <div className="staff-dash-profile-block">
              <div className="staff-dash-profile-row">
                <span className="staff-dash-profile-label">Name</span>
                <span className="staff-dash-profile-value">{displayName}</span>
              </div>
              <div className="staff-dash-profile-row">
                <span className="staff-dash-profile-label">Role</span>
                <span className="staff-dash-profile-value">{displayRole}</span>
              </div>
              <div className="staff-dash-profile-row">
                <span className="staff-dash-profile-label">Workload</span>
                <span className="staff-dash-profile-value">
                  {tasks.length} assigned · {completedCount} completed
                </span>
              </div>
            </div>
          </section>

          <section className="staff-dash-panel">
            <h2 className="staff-dash-panel-title">Status summary</h2>
            <ul className="staff-dash-summary-list">
              <li>
                <span>Pending</span>
                <span className="staff-dash-summary-value">{pendingCount}</span>
              </li>
              <li>
                <span>In progress</span>
                <span className="staff-dash-summary-value">{inProgressCount}</span>
              </li>
              <li>
                <span>Completed</span>
                <span className="staff-dash-summary-value staff-dash-summary-value--success">
                  {completedCount}
                </span>
              </li>
            </ul>
          </section>
        </aside>
      </main>
    </div>
  );
}

export default StaffDashboard;
